import React from 'react';
import { LockClosedIcon } from '@heroicons/react/16/solid';


type TabsOption<T extends string | number> = {
	label: string,
	value: T,
	outlined?: boolean,
	// An icon before the label.
	icon?: React.ReactNode,
	// A short note (for example "No group yet"): the title of the tab, read by a screen reader after the label, and
	// shown in brackets in the select.
	note?: string,
	// The option cannot be selected; it shows a lock, and its note says why.
	disabled?: boolean,
};

type TabsProps<T extends string | number> = {
	options: TabsOption<T>[],
	value: T,
	onChange: (value: T) => void,
	// 'underline' (default): the tabs with a blue line under the selected one.
	// 'pill': a row of buttons with a blue-100 ground on the selected one, for a choice inside a page or a card.
	variant?: 'underline' | 'pill',
	// The accessible name of the tabs. Default: 'Select a tab' for the select (and 'Tabs' for the underline row).
	label?: string,

}

const Tabs = <T extends string | number,>({ options, value, onChange, variant = 'underline', label }: TabsProps<T>) => {

	const activeClassNames = 'whitespace-nowrap border-b-2 border-blue-500 px-3 py-1.5 text-sm font-medium text-blue-600 cursor-pointer';
	const inactiveClassNames = 'whitespace-nowrap border-b-2 border-transparent px-3 py-1.5 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700 cursor-pointer';
	const disabledClassNames = 'whitespace-nowrap border-b-2 border-transparent px-3 py-1.5 text-sm font-medium text-gray-400 cursor-not-allowed';

	const toSelectValue = (v: T) => String(v);

	const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const selected = options.find(o => toSelectValue(o.value) === e.target.value);
		if (selected) {
			onChange(selected.value);
		}
	};

	const optionText = (o: TabsOption<T>) => o.note !== undefined ? `${o.label} (${o.note})` : o.label;

	const content = (o: TabsOption<T>) => <>
		{o.icon !== undefined && <span aria-hidden="true" className="inline-flex shrink-0">{o.icon}</span>}
		{o.outlined ? <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 ring-1 ring-inset ring-blue-700/10">{o.label}</span> : o.label}
		{o.disabled === true && <LockClosedIcon aria-hidden="true" className="h-3 w-3 shrink-0" />}
		{o.note !== undefined && <span className="sr-only"> ({o.note})</span>}
	</>;

	if (variant === 'pill') {
		return <div>
			<div className="sm:hidden">
				<select aria-label={label ?? 'Select a tab'}
								value={toSelectValue(value)}
								onChange={handleSelectChange}
								className="w-full input">
					{options.map(o => <option key={toSelectValue(o.value)} value={toSelectValue(o.value)} disabled={o.disabled === true}>
						{optionText(o)}
					</option>)}
				</select>
			</div>
			<div role="group" aria-label={label ?? 'Select a tab'} className="hidden sm:flex sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-2">
				{options.map(o => {
					const selected = o.value === value;
					const off = o.disabled === true;
					return <button key={toSelectValue(o.value)} type="button" aria-pressed={selected} title={o.note} disabled={off}
												 onClick={() => {
													 if (!selected) onChange(o.value);
												 }}
												 className={[
													 'inline-flex items-center gap-x-1.5 rounded-md px-3 py-1.5 text-sm font-medium focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
													 selected ? 'bg-blue-100 text-blue-700' : 'text-gray-500',
													 off ? 'cursor-not-allowed opacity-50' : (selected ? '' : 'hover:text-gray-700'),
												 ].filter(Boolean).join(' ')}>
						{content(o)}
					</button>;
				})}
			</div>
		</div>;
	}

	return <>
			<div className="grid grid-cols-1 sm:hidden">
				<select aria-label={label ?? 'Select a tab'}
								value={toSelectValue(value)}
								onChange={handleSelectChange}
								className="col-start-1 row-start-1 w-full input">
					{options.map(o => <option key={o.label} id={o.label} value={toSelectValue(o.value)} disabled={o.disabled}>{optionText(o)}</option>)}
				</select>
			</div>
			<div className="hidden sm:block">
				<div className="border-b border-gray-200">
					<nav className="-mb-px flex" aria-label={label ?? 'Tabs'}>
						{options.map(o => <button key={o.label} onClick={() => onChange(o.value)} title={o.note} disabled={o.disabled}
													className={o.disabled === true ? disabledClassNames : ((o.value === value) ? activeClassNames : inactiveClassNames)}>
							{content(o)}
						</button>)}
					</nav>
				</div>
			</div>
	</>;
};

export default Tabs;